import { ObjectStoreMeta, ObjectStoreSchema } from './ngx-indexed-db.meta';

export function openDatabase(
  indexedDB: IDBFactory,
  dbName: string,
  version?: number,
  upgradeCallback?: (a: Event, b: IDBDatabase) => void,
): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (!indexedDB) {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const request = indexedDB.open(dbName, version);
    let db: IDBDatabase;
    request.onsuccess = (event: Event) => {
      db = request.result;
      // Ensure this connection won't block future upgrades
      try {
        db.onversionchange = () => {
          try {
            db.close();
          } catch {}
        };
      } catch {}
      resolve(db);
    };
    request.onerror = (event: Event) => {
      reject(new Error(`IndexedDB error: ${request.error}`));
    };
    request.onblocked = () => {
      // Another open connection (possibly in another tab) is blocking this open/upgrade
      reject(new Error('IndexedDB open blocked by existing connections'));
    };
    if (typeof upgradeCallback === 'function') {
      request.onupgradeneeded = (event: Event) => {
        const upgradingDb = (event.target as IDBOpenDBRequest)
          .result as IDBDatabase;
        // Attach onversionchange safeguard during upgrade as well
        try {
          upgradingDb.onversionchange = () => {
            try {
              upgradingDb.close();
            } catch {}
          };
        } catch {}
        upgradeCallback(event, upgradingDb);
      };
    }
  });
}

export function CreateObjectStore(
  indexedDB: IDBFactory,
  dbName: string,
  version: number,
  storeSchemas: ObjectStoreMeta[],
  migrationFactory?: () => {
    [key: number]: (db: IDBDatabase, transaction: IDBTransaction) => void;
  },
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (!indexedDB) {
      resolve();
      return;
    }
    const request: IDBOpenDBRequest = indexedDB.open(dbName, version);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const database: IDBDatabase = (event.target as any).result;

      storeSchemas.forEach((storeSchema: ObjectStoreMeta) => {
        if (!database.objectStoreNames.contains(storeSchema.store)) {
          const objectStore = database.createObjectStore(
            storeSchema.store,
            storeSchema.storeConfig,
          );
          storeSchema.storeSchema.forEach((schema: ObjectStoreSchema) => {
            objectStore.createIndex(
              schema.name,
              schema.keypath,
              schema.options,
            );
          });
        }
      });

      const storeMigrations = migrationFactory && migrationFactory();
      if (storeMigrations) {
        Object.keys(storeMigrations)
          .map((k) => parseInt(k, 10))
          .filter((v) => v > event.oldVersion)
          .sort((a, b) => a - b)
          .forEach((v) => {
            storeMigrations[v](database, request.transaction!);
          });
      }

      // Don't close here - let onsuccess handle it after transaction completes
    };

    request.onsuccess = (e: any) => {
      e.target.result.close();
      resolve();
    };

    request.onerror = (e: any) => {
      // Surface the error in console to aid diagnostics
      console.error(
        'IndexedDB CreateObjectStore error:',
        e?.target?.error || e,
      );
      reject(
        new Error(
          `IndexedDB CreateObjectStore error: ${e?.target?.error || e}`,
        ),
      );
    };

    request.onblocked = () => {
      // If another connection is open and not closing on versionchange, this upgrade will block.
      // We can't force-close here; just log so callers can implement a retry if needed.
      console.warn(
        'IndexedDB CreateObjectStore blocked by existing connections',
      );
      // Don't reject here - wait for onsuccess or onerror
    };
  });
}
